import React, { useState, useEffect } from 'react';
import { bolAPI } from '../services/millAPI';
import './Orders.css';

interface BOL {
  id: number;
  bol_number: string;
  pro_number: string | null;
  status: string;
  status_display: string;
  ship_date: string;
  consignee_name: string;
  consignee_city: string;
  consignee_state: string;
  carrier: string;
  carrier_display: string;
  total_weight_lbs: number;
  total_pallets: number;
  total_pieces: number;
  order_numbers: string[];
  has_pdf: boolean;
  created_at: string;
}

const BillsOfLading: React.FC = () => {
  const [bols, setBols] = useState<BOL[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingBol, setEditingBol] = useState<any | null>(null);
  const [consigneeForm, setConsigneeForm] = useState({
    consignee_name: '', consignee_address: '', consignee_city: '',
    consignee_state: '', consignee_zip: '', consignee_phone: '',
  });
  // Pickup number + ship date are edited alongside the consignee (same modal, same
  // PATCH). Kept separate from consigneeForm so we only send what changed.
  const [pickupNumber, setPickupNumber] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [savingConsignee, setSavingConsignee] = useState(false);

  useEffect(() => {
    loadBOLs();
  }, [statusFilter]);

  const handleEditConsignee = async (bolId: number) => {
    try {
      const resp = await bolAPI.getById(bolId);
      const b = resp.data;
      setConsigneeForm({
        consignee_name: b.consignee_name || '',
        consignee_address: b.consignee_address || '',
        consignee_city: b.consignee_city || '',
        consignee_state: b.consignee_state || '',
        consignee_zip: b.consignee_zip || '',
        consignee_phone: b.consignee_phone || '',
      });
      setPickupNumber(b.pickup_number || '');
      setShipDate(b.ship_date || '');
      setEditingBol(b);
    } catch {
      setError('Failed to load BOL details for editing');
    }
  };

  const handleSaveConsignee = async () => {
    if (!editingBol) return;
    try {
      setSavingConsignee(true);
      // Send consignee + pickup + ship date together. ship_date/pickup_number set
      // their own _manually_edited flags server-side so they survive regeneration.
      const payload: Record<string, string> = { ...consigneeForm, pickup_number: pickupNumber };
      if (shipDate) payload.ship_date = shipDate;
      await bolAPI.update(editingBol.id, payload);
      setEditingBol(null);
      await loadBOLs();
    } catch {
      setError('Failed to save BOL');
    } finally {
      setSavingConsignee(false);
    }
  };

  const loadBOLs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await bolAPI.getList(statusFilter ? { status: statusFilter } : undefined);
      setBols(response.data.results || []);
    } catch (err: any) {
      console.error('Error loading BOLs:', err);
      setError('Failed to load Bills of Lading');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (bol: BOL) => {
    try {
      const response = await bolAPI.downloadPDF(bol.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${bol.bol_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF');
    }
  };

  const handleMarkDelivered = async (bolId: number) => {
    if (!window.confirm('Mark this shipment as delivered?')) return;

    try {
      await bolAPI.markDelivered(bolId);
      loadBOLs();
    } catch (err: any) {
      console.error('Error marking delivered:', err);
      alert('Failed to update status');
    }
  };

  const handleVoid = async (bolId: number) => {
    const reason = window.prompt('Reason for voiding this BOL:');
    if (!reason) return;

    try {
      await bolAPI.void(bolId, reason);
      loadBOLs();
    } catch (err: any) {
      console.error('Error voiding BOL:', err);
      alert('Failed to void BOL: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return '#6b7280';
      case 'GENERATED': return '#3b82f6';
      case 'PRINTED': return '#8b5cf6';
      case 'SHIPPED': return '#f59e0b';
      case 'DELIVERED': return '#10b981';
      case 'VOIDED': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="orders-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Bills of Lading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Bills of Lading</h1>
          <p className="subtitle">Manage shipping documents</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="filters-section" style={{ marginBottom: '1.5rem' }}>
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="GENERATED">Generated</option>
            <option value="PRINTED">Printed</option>
            <option value="SHIPPED">Shipped</option>
            <option value="DELIVERED">Delivered</option>
            <option value="VOIDED">Voided</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={loadBOLs}>
          Refresh
        </button>
      </div>

      {bols.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Bills of Lading</h3>
          <p>BOLs will appear here when generated from the Mill Shipping page.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>BOL #</th>
                <th>Ship Date</th>
                <th>Consignee</th>
                <th>Carrier</th>
                <th>Orders</th>
                <th>Weight</th>
                <th>Pallets</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bols.map(bol => (
                <tr key={bol.id}>
                  <td>
                    <span className="order-number">{bol.bol_number}</span>
                    {bol.pro_number && (
                      <span style={{ display: 'block', fontSize: '0.8rem', color: '#666' }}>
                        PRO: {bol.pro_number}
                      </span>
                    )}
                  </td>
                  <td>{formatDate(bol.ship_date)}</td>
                  <td>
                    <strong>{bol.consignee_name}</strong>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: '#666' }}>
                      {bol.consignee_city}, {bol.consignee_state}
                    </span>
                  </td>
                  <td>{bol.carrier_display}</td>
                  <td>
                    {(bol.order_numbers || []).map(num => (
                      <span
                        key={num}
                        style={{
                          display: 'inline-block',
                          background: '#e0e7ff',
                          color: '#3730a3',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          marginRight: '4px',
                          marginBottom: '2px',
                        }}
                      >
                        {num}
                      </span>
                    ))}
                  </td>
                  <td>{bol.total_weight_lbs.toLocaleString()} lbs</td>
                  <td>{bol.total_pallets}</td>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '9999px',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        backgroundColor: `${getStatusColor(bol.status)}20`,
                        color: getStatusColor(bol.status),
                      }}
                    >
                      {bol.status_display}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {bol.has_pdf && (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleDownloadPDF(bol)}
                          title="Download PDF"
                        >
                          PDF
                        </button>
                      )}
                      {bol.status === 'SHIPPED' && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleMarkDelivered(bol.id)}
                        >
                          Delivered
                        </button>
                      )}
                      {!['SHIPPED', 'DELIVERED', 'VOIDED'].includes(bol.status) && (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleEditConsignee(bol.id)}
                          title="Edit consignee address, ship date, and pickup number"
                        >
                          Edit
                        </button>
                      )}
                      {!['SHIPPED', 'DELIVERED', 'VOIDED'].includes(bol.status) && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleVoid(bol.id)}
                        >
                          Void
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingBol && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => !savingConsignee && setEditingBol(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: '8px', padding: '1.5rem',
              width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Edit BOL #{editingBol.bol_number}</h3>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '-0.5rem' }}>
              Consignee is the delivery address printed on the carrier document. For LTL
              terminal pickup this should be the terminal (customer c/o terminal).
              Saving overrides autofill and survives regeneration.
            </p>
            {([
              ['consignee_name', 'Name'],
              ['consignee_address', 'Address'],
              ['consignee_city', 'City'],
              ['consignee_state', 'State'],
              ['consignee_zip', 'ZIP'],
              ['consignee_phone', 'Phone'],
            ] as [keyof typeof consigneeForm, string][]).map(([field, label]) => (
              <div key={field} style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '2px' }}>
                  {label}
                </label>
                <input
                  type="text"
                  value={consigneeForm[field]}
                  onChange={e => setConsigneeForm({ ...consigneeForm, [field]: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
            ))}
            <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '1rem 0' }} />
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '2px' }}>
                Ship Date
              </label>
              <input
                type="date"
                value={shipDate}
                onChange={e => setShipDate(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '2px' }}>
                Pickup # <span style={{ fontWeight: 400, color: '#888' }}>(carrier pickup confirmation)</span>
              </label>
              <input
                type="text"
                value={pickupNumber}
                maxLength={100}
                onChange={e => setPickupNumber(e.target.value)}
                placeholder="Carrier pickup confirmation number…"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-secondary" onClick={() => setEditingBol(null)} disabled={savingConsignee}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveConsignee} disabled={savingConsignee}>
                {savingConsignee ? 'Saving…' : 'Save BOL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillsOfLading;
